import { Component, ComponentRef, ViewChild, ViewContainerRef, ElementRef, AfterViewInit, HostListener, OnInit, Injector } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CYModalConfig } from './modal.interface';
import { Type } from '@angular/core';

/**
 * Componente modal reutilizable que permite mostrar otros componentes como modales
 *
 * Características:
 * - Responsive y adaptable a dispositivos móviles
 * - Animaciones suaves de entrada y salida
 * - Cierre con ESC o click en backdrop (configurable)
 * - Overlay con blur para mejor enfoque visual
 * - Configuración flexible de tamaño y comportamiento
 *
 * @example
 * Este componente no se usa directamente, sino a través del CYModalService
 */
@Component({
  selector: 'cy-modal',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="cy-modal-backdrop"
      [class.closing]="isClosing"
      (click)="onBackdropClick()">
      <div class="cy-modal-container"
        #modalContainer
        [class.closing]="isClosing"
        [ngStyle]="getModalStyles()"
        (click)="$event.stopPropagation()">
        <div class="cy-modal-content">
          <ng-container #modalContent></ng-container>
        </div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 1000;
    }

    .cy-modal-backdrop {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(2px);
      display: flex;
      align-items: center;
      justify-content: center;

      /* Animación de entrada */
      animation: fadeIn 0.2s ease-out;
      transition: all 0.25s ease-in-out;
    }

    .cy-modal-backdrop.closing {
      background-color: rgba(0, 0, 0, 0);
      backdrop-filter: blur(0px);
    }

    .cy-modal-container {
      background: var(--background-cy-modal, white);
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      max-width: 90%;
      max-height: 90%;
      overflow: auto;
      position: relative;
      min-width: 300px;
      min-height: 200px;

      /* Animación de entrada */
      animation: slideInScale 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
      transition: all 0.25s ease-in-out;
      transform: scale(1) translateY(0);
    }

    .cy-modal-container.closing {
      animation: slideOutScale 0.25s ease-in-out;
      transform: scale(0.95) translateY(-10px);
      opacity: 0;
    }

    .cy-modal-content {
      padding: 24px;
      width: 100%;
      height: 100%;
    }

    /* Animaciones de entrada */
    @keyframes fadeIn {
      from {
        background-color: rgba(0, 0, 0, 0);
        backdrop-filter: blur(0px);
      }
      to {
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(2px);
      }
    }

    @keyframes slideInScale {
      from {
        opacity: 0;
        transform: scale(0.9) translateY(20px);
      }
      to {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
    }

    /* Animaciones de salida */
    @keyframes slideOutScale {
      from {
        opacity: 1;
        transform: scale(1) translateY(0);
      }
      to {
        opacity: 0;
        transform: scale(0.95) translateY(-10px);
      }
    }

    @media (max-width: 768px) {
      .cy-modal-container {
        max-width: calc(100% - (16px * 2));
        max-height: calc(100% - (16px * 2));
        min-width: unset;
        margin: 16px;
      }

      .cy-modal-content {
        padding: 16px;
      }

      /* Animaciones móviles más suaves */
      @keyframes slideInScale {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(30px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    }
  `]
})
export class CYModalComponent implements OnInit, AfterViewInit {
  /** ViewContainerRef donde se renderizará el componente hijo */
  @ViewChild('modalContent', { read: ViewContainerRef, static: true })
  modalContent!: ViewContainerRef;

  /** Referencia al contenedor del modal para manipulación de estilos */
  @ViewChild('modalContainer', { static: true })
  modalContainer!: ElementRef;

  /** Configuración del modal */
  config: CYModalConfig = {};

  /** Estado de la animación de cierre */
  isClosing: boolean = false;

  ngOnInit() {
    this.applyConfigStyles();
  }

  ngAfterViewInit() {
    this.handleResponsiveLayout();
  }

  @HostListener('window:resize')
  onResize() {
    this.handleResponsiveLayout();
  }

  /**
   * Maneja el cierre del modal con la tecla ESC
   * @param event - Evento del teclado
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    if (!this.config.disableClose) {
      this.closeWithAnimation();
    }
  }

  /**
   * Aplica las clases CSS personalizadas del config al contenedor del modal
   * @private
   */
  private applyConfigStyles() {
    if (this.config.panelClass && this.modalContainer) {
      const classes = Array.isArray(this.config.panelClass)
        ? this.config.panelClass
        : [this.config.panelClass];

      classes.forEach(className => {
        this.modalContainer?.nativeElement?.classList.add(className);
      });
    }
  }

  /**
   * Ajusta el layout del modal para dispositivos responsivos
   * @private
   */
  private handleResponsiveLayout() {
    if (this.modalContainer) {
      const container = this.modalContainer.nativeElement;
      const rect = container.getBoundingClientRect();

      // Ajustar si el modal es muy alto
      if (rect.height > window.innerHeight * 0.9) {
        container.style.maxHeight = '90dvh';
        container.style.overflowY = 'auto';
      }
    }
  }

  /**
   * Crea y adjunta un componente dinámicamente dentro del modal
   * @param component - Tipo del componente a crear
   * @param injector - Injector opcional para el componente (permite inyectar contexto del modal)
   * @returns ComponentRef del componente creado
   */
  attachComponent<T>(component: Type<T>, injector?: Injector): ComponentRef<T> {
    if (!this.modalContent) {
      throw new Error('ViewContainerRef not available');
    }

    this.modalContent.clear();

    // Crear el componente con el injector personalizado si se proporciona
    const componentRef = injector
      ? this.modalContent.createComponent(component, { injector })
      : this.modalContent.createComponent(component);

    return componentRef;
  }

  /**
   * Maneja el click en el backdrop (fondo del modal)
   * Cierra el modal si disableClose no está activo
   */
  onBackdropClick() {
    if (!this.config.disableClose) {
      this.closeWithAnimation();
    }
  }

  /**
   * Inicia la animación de cierre del modal
   * Ejecuta la animación CSS y luego llama al método close después del delay
   */
  closeWithAnimation() {
    if (this.isClosing) return; // Evitar múltiples llamadas

    this.isClosing = true;

    // Esperar a que termine la animación antes de cerrar realmente
    setTimeout(() => {
      this.close();
    }, 250); // 250ms coincide con la duración de la animación CSS
  }

  /**
   * Método close que será sobrescrito por el servicio
   * Incluye implementación por defecto para casos edge
   */
  close() {
    // Este método será sobrescrito por el servicio
    console.warn('Modal close method not properly configured');
  }

  /**
   * Genera los estilos inline del modal basados en la configuración
   * @returns Objeto con estilos CSS
   */
  getModalStyles(): { [key: string]: string } {
    const styles: { [key: string]: string } = {};

    if (this.config.width) {
      styles['width'] = this.config.width;
    }

    if (this.config.height) {
      styles['height'] = this.config.height;
    }

    if (this.config.maxWidth) {
      styles['maxWidth'] = this.config.maxWidth;
    }

    if (this.config.maxHeight) {
      styles['maxHeight'] = this.config.maxHeight;
    }

    return styles;
  }
}
